import { FC, useEffect, useRef, useState } from "react";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import _ from "lodash";
import { fetchGetAllParentCompanies } from "../../user-management/miscellaneous/fetchGetAllParentCompanies";
import { fetchGetUsersForDropdown } from "../miscellaneous/fetchGetUsersForDropdown";
import {
  Autocomplete,
  Checkbox,
  Collapse,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import { ButtonCancelA } from "../../common/components/ButtonCancelA";
import { SearchButton } from "../../common/components/SearchButton";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { fetchGetJobsByCompanyId } from "../../jobs-management/miscellaneous/fetchGetJobsByCompanyId";
import dayjs from "dayjs";
import { fetchGetCompaniesByParent } from "../../organization-management/miscellaneous/fetchGetCompaniesByParent";
import { TaskSubmissionCriteria } from "../miscellaneous/taskSubmissionSearchAtom";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type SearchTasksProps = {
  taskSubmitSearch: any;
  setTaskSubmitSearch: any;
  setPage: any;
  defaultValues?: TaskSubmissionCriteria;
  onAtomReset?: () => void;
};

export const TaskSubmissionAdminSearch: FC<SearchTasksProps> = ({
  taskSubmitSearch,
  setTaskSubmitSearch,
  setPage,
  defaultValues,
  onAtomReset,
}) => {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
  } = useForm<FieldValues>({
    defaultValues: {
      parentCompanyId: defaultValues?.parentCompanyId ?? "",
      organization: defaultValues?.companyId ?? "",
      submittedByUserId: defaultValues?.submittedByUserId ?? "",
      taskId: defaultValues?.taskId ? String(defaultValues.taskId) : "",
      jobTitle: defaultValues?.jobTitleObjects ?? [],
    },
  });

  // Default filter state
  const [parentCompanies, setParentCompanies] = useState<{ id: string; name: string }[]>([]);
  const [organizationsName, setOrganizationsName] = useState<{ id: string; name: string }[]>([]);
  const [jobTitle, setJobTitle] = useState<{ id: string; title: string }[]>([]);
  const [jobTitleSelected, setJobTitleSelected] = useState<{ id: string; title: string }[]>(
    defaultValues?.jobTitleObjects ?? []
  );

  // Advanced filter state
  const [users, setUsers] = useState([]);
  const [fromDate, setFromDate] = useState<any>(
    defaultValues?.from ? dayjs(defaultValues.from) : null
  );
  const [toDate, setToDate] = useState<any>(
    defaultValues?.to ? dayjs(defaultValues.to) : null
  );
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const userRole = !!localStorage.getItem("rolesJson")
    ? JSON.parse(localStorage.getItem("rolesJson") as string)
    : [];

  const isRoleSupperAdmin =
    userRole &&
    userRole.length > 0 &&
    userRole[0]?.Name &&
    userRole[0]?.Name.toLowerCase() === "super admin";

  const isRoleAdmin =
    userRole &&
    userRole.length > 0 &&
    userRole[0]?.Name &&
    userRole[0]?.Name.toLowerCase() === "admin";

  const callerParentCompanyId = localStorage.getItem("parentCompanyId") ?? "";
  const callerParentCompanyName = localStorage.getItem("parentCompanyName") ?? "";

  // Ref to prevent overwriting restored job title selection on initial load
  const hasRestoredJobTitlesRef = useRef(false);

  // ── Lookups ──────────────────────────────────────────────────────────────

  const mutationGetParentCompanies = useMutation(fetchGetAllParentCompanies, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const data = _.map(res.data.data ?? [], (item: any) => ({
          id: item.id,
          name: item.companyName,
        }));
        setParentCompanies(data as any);
      }
    },
  });

  const mutationGetOrganizations = useMutation(fetchGetCompaniesByParent, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const orgs = _.map(res.data.data, (item: any) => ({
          id: item.id,
          name: item.companyName,
        }));
        setOrganizationsName(orgs);
      }
    },
    onError: () => { },
  });

  const mutationGetJobsByCompany = useMutation(fetchGetJobsByCompanyId, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const jobs = _.map(res.data.data ?? [], (item: any) => ({
          id: item.id,
          title: item.title,
        }));
        setJobTitle(jobs as any);
        // On initial load, restore saved selection; afterwards reset on company change
        if (!hasRestoredJobTitlesRef.current && defaultValues?.jobTitleObjects && defaultValues.jobTitleObjects.length > 0) {
          setJobTitleSelected(defaultValues.jobTitleObjects);
          hasRestoredJobTitlesRef.current = true;
        } else if (hasRestoredJobTitlesRef.current) {
          setJobTitleSelected([]);
        } else {
          hasRestoredJobTitlesRef.current = true;
          setJobTitleSelected([]);
        }
      }
    },
  });

  const mutationGetUsers = useMutation(fetchGetUsersForDropdown, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const data = _.map(res.data.data ?? [], (item: any) => ({
          id: item.id,
          label: item.userName ?? item.email,
        }));
        setUsers(data as any);
      }
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleParentCompanyChange = (parentCompanyId: string) => {
    setValue("parentCompanyId", parentCompanyId);
    setValue("organization", "");
    setValue("jobTitle", []);
    setValue("submittedByUserId", "");
    setJobTitle([]);
    setJobTitleSelected([]);
    mutationGetOrganizations.mutate(parentCompanyId || undefined);
    mutationGetUsers.mutate(parentCompanyId || undefined);
  };

  const handleOrganizationChange = (organizationId: string) => {
    setValue("organization", organizationId);
    setValue("jobTitle", []);
    setJobTitleSelected([]);
    if (organizationId) {
      mutationGetJobsByCompany.mutate(organizationId);
    } else {
      setJobTitle([]);
    }
  };

  const handleReset = () => {
    setPage(0);
    reset({
      parentCompanyId: isRoleAdmin ? callerParentCompanyId : "",
      organization: "",
      jobTitle: [],
      submittedByUserId: "",
      taskId: "",
    });
    setJobTitle([]);
    setJobTitleSelected([]);
    setFromDate(null);
    setToDate(null);
    mutationGetOrganizations.mutate(undefined);
    mutationGetUsers.mutate(undefined);
    setTaskSubmitSearch({
      taskId: 0,
      taskName: "",
      dateCompleted: null,
      from: null,
      to: null,
      companyId: "",
      jobTitles: null,
      jobTitleObjects: [],
      parentCompanyId: "",
      submittedByUserId: "",
    });
    onAtomReset?.();
  };

  const onSearchSubmit = (data: any) => {
    setPage(0);
    const jobTitleIds = _.map(data.jobTitle ?? [], "id");

    const currentTime = dayjs()
      .format()
      .substring(dayjs().format().lastIndexOf("T") + 1, dayjs().format().length);

    const fromTime = !!fromDate
      ? `${(fromDate as any).format().substring(0, dayjs().format().lastIndexOf("T") + 1)}${currentTime}`
      : null;
    const toTime = !!toDate
      ? `${(toDate as any).format().substring(0, dayjs().format().lastIndexOf("T") + 1)}${currentTime}`
      : null;

    const newPayload = {
      ...taskSubmitSearch,
      parentCompanyId: data.parentCompanyId ?? "",
      companyId: data.organization ?? "",
      jobTitles: jobTitleIds,
      jobTitleObjects: data.jobTitle ?? [],
      submittedByUserId: data.submittedByUserId ?? "",
      from: !!fromDate ? fromTime : null,
      to: !!toDate ? toTime : null,
      taskId: !!data.taskId ? parseInt(data.taskId, 10) : 0,
    };

    setTaskSubmitSearch(newPayload);
  };

  // ── Initial load ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (isRoleSupperAdmin) {
      mutationGetParentCompanies.mutate();
      mutationGetOrganizations.mutate("");

    } else if (isRoleAdmin) {
      setParentCompanies([{ id: callerParentCompanyId, name: callerParentCompanyName }]);
      setValue("parentCompanyId", callerParentCompanyId);
      mutationGetOrganizations.mutate(callerParentCompanyId);
    }
    mutationGetJobsByCompany.mutate("");
    mutationGetUsers.mutate(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="mt-9 border-2 pt-7 pb-7">
      <form onSubmit={handleSubmit(onSearchSubmit)}>
        {/* ── Default filters (always visible) ── */}
        <div className="grid grid-cols-3 gap-4 px-6">
          {/* Company (ParentCompany) */}
          <div className="flex items-center gap-3">
            <label className="font-bold w-28 shrink-0 text-right">Company</label>
            <Controller
              control={control}
              name="parentCompanyId"
              render={({ field: { value } }) => (
                <Select
                  id="submission-parentCompanyId"
                  value={value ?? ""}
                  onChange={(e) => handleParentCompanyChange(e.target.value)}
                  style={{ height: "38px", width: "100%", minWidth: "200px" }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {(parentCompanies ?? []).map((item: any) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </div>

          {/* Organization */}
          <div className="flex items-center gap-3">
            <label className="font-bold w-28 shrink-0 text-right">Organization</label>
            <Controller
              control={control}
              name="organization"
              defaultValue=""
              render={({ field: { value } }) => (
                <Select
                  id="submission-organization"
                  value={value ?? ""}
                  onChange={(e) => handleOrganizationChange(e.target.value)}
                  style={{ height: "38px", width: "100%", minWidth: "200px" }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {(organizationsName ?? []).map((item: any) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </div>

          {/* Job Title */}
          <div className="flex items-center gap-3">
            <label className="font-bold w-28 shrink-0 text-right">Job Title</label>
            <Controller
              control={control}
              name="jobTitle"
              defaultValue={[]}
              render={() => (
                <Autocomplete
                  multiple
                  id="checkboxes-tags"
                  size="small"
                  options={jobTitle ?? []}
                  value={jobTitleSelected ?? []}
                  disableCloseOnSelect
                  getOptionLabel={(option: any) => option.title}
                  isOptionEqualToValue={(option: any, value) =>
                    value === undefined || option.id === value.id
                  }
                  onChange={(_, data) => {
                    setValue("jobTitle", data);
                    setJobTitleSelected(data as any);
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.title}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params}
                      size="small"
                      sx={{ width: "100%", minWidth: "200px" }} />
                  )}
                  sx={{
                    width: "100%",
                    "& .MuiInputBase-root": {
                      minHeight: "38px",
                    },
                    "& .MuiChip-root": {
                      height: "24px",
                    },
                  }}
                />
              )}
            />
          </div>
        </div>

        {/* ── Advanced filters (collapsible) ── */}
        <Collapse in={isAdvancedOpen}>
          <div className="grid grid-cols-3 gap-4 px-6 mt-5">
            {/* User Name */}
            <div className="flex items-center gap-3">
              <label className="font-bold w-28 shrink-0 text-right">User Name</label>
              <Controller
                control={control}
                name="submittedByUserId"
                defaultValue=""
                render={({ field: { value } }) => (
                  <Select
                    id="submission-submittedByUserId"
                    value={value ?? ""}
                    onChange={(e) => setValue("submittedByUserId", e.target.value)}
                    style={{ height: "38px", width: "100%", minWidth: "200px" }}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {(users ?? []).map((item: any) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </div>

            {/* From */}
            <div className="flex items-center gap-3">
              <label className="font-bold w-28 shrink-0 text-right">From</label>
              <Controller
                name="from"
                control={control}
                defaultValue={fromDate}
                render={() => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={fromDate}
                      onChange={(newValue) => setFromDate(newValue)}
                      sx={{
                        "& .MuiInputBase-input": { height: "5px", width: "100%" },
                        width: "100%",
                        minWidth: "200px",
                      }}
                    />
                  </LocalizationProvider>
                )}
              />
            </div>

            {/* To */}
            <div className="flex items-center gap-3">
              <label className="font-bold w-28 shrink-0 text-right">To</label>
              <Controller
                name="to"
                control={control}
                defaultValue={toDate}
                render={() => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={toDate}
                      onChange={(newValue) => setToDate(newValue)}
                      sx={{
                        "& .MuiInputBase-input": { height: "5px", width: "100%" },
                        width: "100%",
                        minWidth: "200px",
                      }}
                    />
                  </LocalizationProvider>
                )}
              />
            </div>

            {/* Task ID */}
            <div className="flex items-center gap-3">
              <label className="font-bold w-28 shrink-0 text-right">Task ID</label>
              <TextField
                type="number"
                inputProps={{ min: 0 }}
                {...register("taskId")}
                sx={{
                  width: "100%",
                  minWidth: "200px",
                  "& .MuiInputBase-input": {
                    height: "5px",
                  }
                }}
              />
            </div>
          </div>
        </Collapse>

        {/* ── Advance Filter toggle ── */}
        <div className="flex justify-end mt-5 mr-5">
          <button
            type="button"
            onClick={() => setIsAdvancedOpen((prev) => !prev)}
            className="flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-800"
          >
            Advance Filter
            {isAdvancedOpen ? <KeyboardDoubleArrowUpIcon fontSize="small" /> : <KeyboardDoubleArrowDownIcon fontSize="small" />}
          </button>
        </div>

        {/* ── Actions ── */}
        <div className="flex flex-center justify-center mt-10 gap-5">
          <ButtonCancelA
            name="Reset"
            onHandleClick={handleReset}
            icon={<ClearIcon />}
          />
          <SearchButton />
        </div>
      </form>
    </div>
  );
};
